import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import PageHeader from "@/components/common/PageHeader";
import { toast } from "sonner";
import { Upload, FileText, Save, ExternalLink, Loader2, Heading, AlignLeft, AlignJustify, Camera, File } from "lucide-react";
import { profileAvatar } from "@/data/mockData";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BACKEND_URL } from "@/config";

const getMediaUrl = (path) => {
  const backendUrl = BACKEND_URL;
  return path ? (path.startsWith('http') ? path : `${backendUrl}${path}`) : "";
};

export default function AboutMe() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    heading: "",
    shortDescription: "",
    detailedDescription: "",
    profileImageUrl: "",
    resumeUrl: ""
  });
  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageName, setImageName] = useState("No file selected");
  const [resumeName, setResumeName] = useState("No file selected");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/AboutMe');
        if (res.success && res.data && res.data.length > 0) {
          const data = res.data[0];
          setRecordId(data.id);
          setForm({
            heading: data.heading || "",
            shortDescription: data.shortDescription || "",
            detailedDescription: data.detailedDescription || "",
            profileImageUrl: data.profileImageUrl || "",
            resumeUrl: data.resumeUrl || ""
          });
          if (data.profileImageUrl) {
            setImageName(data.profileImageUrl.split('/').pop());
          }
          if (data.resumeUrl) {
            setResumeName(data.resumeUrl.split('/').pop());
          }
        }
      } catch (err) {
        toast.error("Failed to load About Me data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageName(file.name);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post('/AboutMe/upload-profile-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.success) {
        setForm({ ...form, profileImageUrl: res.data });
        toast.success("Profile image uploaded");
      }
    } catch (err) {
      toast.error(err.message || "Failed to upload image");
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResumeName(file.name);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post('/AboutMe/upload-resume', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.success) {
        setForm({ ...form, resumeUrl: res.data });
        toast.success("Resume uploaded");
      }
    } catch (err) {
      toast.error(err.message || "Failed to upload resume");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (recordId) {
        const res = await api.put(`/AboutMe/${recordId}`, form);
        if (res.success) toast.success("About Me section updated.");
      } else {
        const res = await api.post('/AboutMe', form);
        if (res.success) {
          setRecordId(res.data);
          toast.success("About Me section created.");
        }
      }
    } catch (err) {
      toast.error(err.message || "Failed to save data");
    }
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="About Me"
        description="Tell the world who you are. This content powers your portfolio's hero section."
        testId="about"
      />

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Form */}
        <form onSubmit={handleSave} className="xl:col-span-3 space-y-4" data-testid="about-form">
          <Card className="p-6 space-y-5">
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <Heading className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
                <h3 className="font-display text-sm font-semibold">Headline</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-3">A single sentence that sets the tone.</p>
              <Input
                data-testid="about-heading"
                placeholder="Senior Product Engineer, Folio Admin"
                value={form.heading}
                onChange={(e) => setForm({ ...form, heading: e.target.value })}
              />
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <AlignLeft className="h-3.5 w-3.5" strokeWidth={2} />
                Short Description
              </Label>
              <Textarea
                data-testid="about-short-desc"
                value={form.shortDescription}
                onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
                rows={3}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <AlignJustify className="h-3.5 w-3.5" strokeWidth={2} />
                Detailed Description
              </Label>
              <Textarea
                data-testid="about-detailed-desc"
                value={form.detailedDescription}
                onChange={(e) => setForm({ ...form, detailedDescription: e.target.value })}
                rows={12}
                className="mt-1.5"
              />
            </div>
          </Card>

          <Card className="p-6 space-y-5">
            <h3 className="font-display text-sm font-semibold">Media</h3>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Camera className="h-3.5 w-3.5" strokeWidth={2} />
                Profile Image
              </Label>
              <div className="mt-1.5 flex items-center gap-3">
                <Avatar className="h-14 w-14 rounded-lg">
                  <AvatarImage src={form.profileImageUrl ? getMediaUrl(form.profileImageUrl) : ""} className="rounded-lg" />
                  <AvatarFallback className="rounded-lg">{user?.username?.substring(0, 2).toUpperCase() || "AM"}</AvatarFallback>
                </Avatar>
                <div className="flex-1 flex items-center gap-2">
                  <label className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-between border-2 border-dashed border-border/60 bg-muted/30 rounded-xl px-4 py-3 hover:border-primary/50 hover:bg-primary/5 transition-all duration-300 group">
                      <span className="text-sm font-mono text-muted-foreground truncate group-hover:text-foreground transition-colors">{imageName}</span>
                      <span className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1.5 rounded-md inline-flex items-center gap-1.5 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Upload className="h-3.5 w-3.5" strokeWidth={2} />
                        Upload
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="about-image-upload"
                      onChange={handleImageUpload}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <File className="h-3.5 w-3.5" strokeWidth={2} />
                Resume / CV
              </Label>
              <label className="mt-1.5 block cursor-pointer group">
                <div className="flex items-center justify-between border-2 border-dashed border-border/60 bg-muted/30 rounded-xl px-4 py-4 hover:border-primary/50 hover:bg-primary/5 transition-all duration-300">
                  <div className="flex items-center gap-4">
                    <span className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform">
                      <FileText className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="text-sm font-semibold group-hover:text-primary transition-colors">{resumeName}</p>
                      <p className="text-[11px] font-mono text-muted-foreground">PDF Document</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1.5 rounded-md inline-flex items-center gap-1.5 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Upload className="h-3.5 w-3.5" strokeWidth={2} />
                    Replace
                  </span>
                </div>
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  data-testid="about-resume-upload"
                  onChange={handleResumeUpload}
                />
              </label>
            </div>
          </Card>

          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" type="button" data-testid="about-cancel">Cancel</Button>
            <Button type="submit" data-testid="about-save">
              <Save className="mr-1.5 h-4 w-4" strokeWidth={1.75} />
              Save changes
            </Button>
          </div>
        </form>

        {/* Live preview */}
        <div className="xl:col-span-2">
          <div className="xl:sticky xl:top-24 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Live preview
            </p>
            <Card className="p-0 overflow-hidden border-border/50 shadow-sm" data-testid="about-preview">
              <div className="h-32 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-orange-500 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent bottom-0 h-full mt-auto" />
              </div>
              <div className="p-6 -mt-14 relative z-10">
                <Avatar className="h-24 w-24 rounded-2xl border-4 border-background shadow-xl shadow-black/5 ring-1 ring-border/10">
                  <AvatarImage src={form.profileImageUrl ? getMediaUrl(form.profileImageUrl) : ""} className="rounded-2xl object-cover" />
                  <AvatarFallback className="rounded-2xl text-2xl font-display font-bold bg-primary/5 text-primary">
                    {user?.username?.substring(0, 2).toUpperCase() || "AM"}
                  </AvatarFallback>
                </Avatar>
                
                <h3 className="font-display text-2xl font-bold tracking-tight mt-5">{user?.username || "Admin"}</h3>
                <Badge variant="secondary" className="mt-2 font-mono text-[10px] bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors">
                  {form.heading || "Senior Product Engineer"}
                </Badge>
                
                <p className="text-sm mt-5 leading-relaxed text-foreground/90 font-medium">
                  {form.shortDescription || "Add a short description to see it previewed here."}
                </p>
                
                <div className="h-px w-full bg-gradient-to-r from-border via-border/50 to-transparent my-5" />
                
                <p className="text-[13px] text-muted-foreground leading-relaxed line-clamp-4">
                  {form.detailedDescription || "Your detailed description will appear here..."}
                </p>
                
                <Button asChild className="mt-6 w-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-md hover:shadow-lg hover:shadow-primary/20 transition-all duration-300">
                  <a href="https://shivajirajan.com/" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" strokeWidth={2} />
                    View public portfolio
                  </a>
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
